import { useState, type ComponentProps, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useResizeObserverRef } from "@wojtekmaj/react-hooks";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import {
  findInstrumentDisplayName,
  type Assessment,
  type AssessmentId,
  type DeleteAssessmentRequest,
  type GuestAsk,
  type PersonInvolvementId,
  type PersonState,
} from "@thalamos/haven-common";

import { createRoute } from "../../routing/createRoute.js";
import {
  ACTION_TABLE_MIN_WIDTH,
  ActionTable,
} from "../ActionTable/ActionTable.js";
import {
  AskGuestDialog,
  calendarDayText,
} from "../AskGuestDialog/AskGuestDialog.js";
import { AssessmentKindChip } from "../AssessmentKindChip/AssessmentKindChip.js";
import { AssessmentStatusChip } from "../AssessmentStatusChip/AssessmentStatusChip.js";
import {
  DeleteAssessmentDialog,
  type DeletionOutcome,
  type ProgressReading,
} from "../DeleteAssessmentDialog/DeleteAssessmentDialog.js";
import {
  EndGuestAccessDialog,
  type GuestAccessEndingOutcome,
} from "../EndGuestAccessDialog/EndGuestAccessDialog.js";
import { GuestAssessor, GuestChip } from "../GuestAssessor/GuestAssessor.js";
import {
  StartAssessmentDialog,
  type DefinitiveStart,
  type PreviousAssessment,
  type StartOutcome,
} from "../StartAssessmentDialog/StartAssessmentDialog.js";
import { useToast } from "../ToastProvider/ToastProvider.js";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

/** Why a current assessment offers this session nothing. */
export type NoActionReason = "assessor_only" | "approver_only";

/**
 * What this session may do with one assessment in the workflow, and why not
 * where it may do nothing.
 */
export type CurrentAssessmentActions = {
  /** Carry on filling it in, or reopen it once returned. */
  continue: boolean;
  /** Read it, as it stands, while it waits for a decision. */
  view: boolean;
  /** Read it in order to approve or return it. */
  review: boolean;
  /** Take it out of use, giving a reason. */
  delete: boolean;
  /** Set exactly where none of the above is. */
  why: NoActionReason | undefined;
};

/** One assessment in the workflow, as this session sees it. */
export type CurrentAssessmentRow = {
  assessment: Assessment;
  /** Undefined where the screen cannot tell; see `assessorNameOf`. */
  assessorName: string | undefined;
  /** Undefined where nobody approves the assessor's work yet. */
  approverName: string | undefined;
  /** Who asked for it, where a guest owns it; see `guestAskOf`. */
  guestAsk?: GuestAsk | undefined;
  actions: CurrentAssessmentActions;
};

/** Starting an assessment, where this session may start one here. */
export type AssessmentStarting = {
  /** Who the assessment is about, as the dialog names them. */
  personName: string;
  /** Present where the session starts the definitive assessment. */
  definitive?: DefinitiveStart | undefined;
  /**
   * Whether a definitive assessment is already in the workflow, which stops
   * the session starting another where its start would be definitive.
   */
  definitiveUnderWay: boolean;
  /** The versions the acting body may use; undefined until they have been read. */
  versions: readonly string[] | undefined;
  /** Whether reading the versions failed, so they will not arrive by waiting. */
  versionsFailed: boolean;
  /** The assessment a start would follow, where ratings could carry over from one. */
  previous?: PreviousAssessment | undefined;
  onStart: (version: string, carryOver: boolean) => Promisable<StartOutcome>;
};

/** Asking somebody outside the service to assess, where this session may. */
export type GuestAsking = Pick<
  ComponentProps<typeof AskGuestDialog>,
  "versions" | "versionsFailed" | "onMatch" | "onAsk"
>;

/** Ending a guest's access, where this session may. */
export type GuestEnding = {
  onEnd: (
    involvementId: PersonInvolvementId,
  ) => Promisable<GuestAccessEndingOutcome>;
};

/**
 * The guest asked to complete a row's open assessment, while their involvement
 * has not ended; one past their end date included, since End also discards
 * the answers they never sent.
 */
const endableGuestAsk = ({
  assessment,
  guestAsk,
}: CurrentAssessmentRow): GuestAsk | undefined =>
  guestAsk !== undefined &&
  guestAsk.endsAt !== null &&
  assessment.status === "open"
    ? guestAsk
    : undefined;

const COLUMNS = [
  "kind",
  "instrument",
  "assessor",
  "approver",
  "state",
  "started",
] as const;

/** What each column of a row shows, shared by the table and its stacked form. */
const useCells = ({
  assessment,
  assessorName,
  approverName,
  guestAsk,
}: CurrentAssessmentRow) => {
  const { t } = useTranslation();
  const name = assessorName ?? t("personPage.assessments.unknownAssessor");

  return {
    kind: assessment.definitive ? (
      <AssessmentKindChip definitive />
    ) : (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        <AssessmentKindChip definitive={false} />
        {guestAsk === undefined ? null : <GuestChip />}
      </Stack>
    ),
    instrument: findInstrumentDisplayName(assessment.instrument.name) ?? null,
    assessor:
      guestAsk === undefined ? (
        name
      ) : (
        <GuestAssessor
          name={name}
          guestAsk={guestAsk}
          withGuest={assessment.status === "open"}
        />
      ),
    // A non-definitive assessment never goes for approval, so it has no
    // Approver to name, not one still to be found.
    approver: assessment.definitive
      ? (approverName ?? t("personPage.assessments.noApprover"))
      : null,
    state: <AssessmentStatusChip status={assessment.status} />,
    started: CALENDAR_DATE.format(assessment.startedAt),
  } satisfies Record<(typeof COLUMNS)[number], ReactNode>;
};

/**
 * The actions that open the assessment, all in its own screens, which show
 * what its status allows.
 */
const OPENINGS = ["continue", "view", "review"] as const;

/** The handlers a row's actions call, each already bound to that row. */
type RowHandlers = {
  onDelete: () => void;
  /** Absent where the session may not end the guest's access on this row. */
  onEndGuestAccess?: (() => void) | undefined;
};

/** End on a row whose assessment a guest is still completing. */
const EndGuestAccessButton = ({
  guestName,
  onEnd,
}: {
  guestName: string;
  onEnd: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <Button
      variant="outlined"
      size="small"
      color="error"
      aria-label={t("endGuestAccess.openOf", { name: guestName })}
      onClick={onEnd}
    >
      {t("endGuestAccess.open")}
    </Button>
  );
};

const RowActions = ({
  row: { assessment, assessorName, actions },
  onDelete,
  onEndGuestAccess,
}: { row: CurrentAssessmentRow } & RowHandlers) => {
  const { t } = useTranslation();
  const name = assessorName ?? t("personPage.assessments.unknownAssessor");
  const endGuestAccess =
    onEndGuestAccess === undefined ? null : (
      <EndGuestAccessButton guestName={name} onEnd={onEndGuestAccess} />
    );

  if (actions.why !== undefined) {
    return (
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Typography variant="body2" color="text.secondary">
          {t(`personPage.assessments.why.${actions.why}`)}
        </Typography>
        {endGuestAccess}
      </Stack>
    );
  }

  const whose = {
    assessor: name,
    date: CALENDAR_DATE.format(assessment.startedAt),
  };

  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      {OPENINGS.filter((opening) => actions[opening]).map((opening) => (
        <Button
          key={opening}
          variant="outlined"
          size="small"
          component={RouterLink}
          to={createRoute.assessment(assessment.id)}
          aria-label={t(`personPage.assessments.${opening}Of`, whose)}
        >
          {t(`personPage.assessments.${opening}`)}
        </Button>
      ))}
      {actions.delete ? (
        <Button
          variant="outlined"
          size="small"
          color="error"
          aria-label={t("personPage.assessments.deleteOf", whose)}
          onClick={onDelete}
        >
          {t("personPage.assessments.delete")}
        </Button>
      ) : null}
      {endGuestAccess}
    </Stack>
  );
};

const TableLayoutRow = ({
  row,
  ...handlers
}: { row: CurrentAssessmentRow } & RowHandlers) => {
  const cells = useCells(row);

  return (
    <TableRow>
      {COLUMNS.map((column) => (
        <TableCell key={column}>{cells[column]}</TableCell>
      ))}
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <RowActions row={row} {...handlers} />
      </TableCell>
    </TableRow>
  );
};

const StackedRow = ({
  row,
  ...handlers
}: { row: CurrentAssessmentRow } & RowHandlers) => {
  const { t } = useTranslation();
  const cells = useCells(row);

  return (
    <Stack spacing={1} sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {cells.kind}
        <Typography variant="subtitle2">{cells.state}</Typography>
      </Stack>
      {(["instrument", "assessor", "approver", "started"] as const).map(
        (column) =>
          cells[column] === null ? null : (
            <Typography key={column} variant="body2">
              {t(`personPage.assessments.column.${column}`)}: {cells[column]}
            </Typography>
          ),
      )}
      <Box>
        <RowActions row={row} {...handlers} />
      </Box>
    </Stack>
  );
};

const EmptyState = ({
  state,
  caseInvolvementHref,
}: {
  state: PersonState;
  caseInvolvementHref: string;
}) => {
  const { t } = useTranslation();

  return match(state)
    .with({ kind: "active" }, () => (
      <Typography variant="body2" color="text.secondary">
        {t("personPage.assessments.empty.active")}
      </Typography>
    ))
    .with({ kind: "unassigned" }, ({ missing }) => (
      <Stack spacing={0.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          {t("personPage.assessments.empty.unassigned", {
            missing: missing.map((kind) => t(`access.kind.${kind}`)).join(", "),
          })}
        </Typography>
        <Link component={RouterLink} to={caseInvolvementHref} variant="body2">
          {t("personPage.assessments.goToCaseInvolvement")}
        </Link>
      </Stack>
    ))
    .with({ kind: "archived" }, () => (
      <Typography variant="body2" color="text.secondary">
        {t("personPage.assessments.empty.archived")}
      </Typography>
    ))
    .exhaustive();
};

/**
 * A young person's assessments still in the workflow, each with what this
 * session may do with it, and the way to start another.
 *
 * Each action is shown only where the session may take it, and a row offering
 * none says why, so nobody follows a link that ends in a refusal.
 */
export const CurrentAssessments = ({
  rows,
  failed,
  state,
  starting,
  guestAsking,
  guestEnding,
  caseInvolvementHref,
  personName,
  readProgress,
  onDelete,
}: {
  /** In the order to show them; undefined until read, which is not the same as none. */
  rows: CurrentAssessmentRow[] | undefined;
  failed: boolean;
  /** Undefined until the record and its involvements have both been read. */
  state: PersonState | undefined;
  /** Absent where the session may not start an assessment here. */
  starting?: AssessmentStarting | undefined;
  /** Absent where the session may not ask a guest to assess here. */
  guestAsking?: GuestAsking | undefined;
  /** Absent where the session may not end a guest's access here. */
  guestEnding?: GuestEnding | undefined;
  /** Where the roles an unassigned profile needs are filled. */
  caseInvolvementHref: string;
  /** Whose record this is; absent where that cannot be told. */
  personName: string | undefined;
  /** How far an assessment has got, for the dialog that deletes it. */
  readProgress: (assessmentId: AssessmentId) => Promisable<ProgressReading>;
  onDelete: (deletion: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }) => Promisable<DeletionOutcome>;
}) => {
  const { t } = useTranslation();
  const [ref, entry] = useResizeObserverRef<HTMLDivElement>();
  // Judged by the component's own width rather than the viewport's, so it also
  // stacks when laid out in a narrow container. Unmeasured reads as wide.
  const width = entry?.contentRect.width;
  const stacked = width !== undefined && width < ACTION_TABLE_MIN_WIDTH;
  // The row itself rather than an id: the server announces a deletion before
  // it answers, so a re-read can drop the row while its request is in flight,
  // and the dialog has to stay open until that request answers.
  const [deleting, setDeleting] = useState<CurrentAssessmentRow | null>(null);
  const [choosingVersion, setChoosingVersion] = useState(false);
  const [startInFlight, setStartInFlight] = useState(false);
  const [askingGuest, setAskingGuest] = useState(false);
  // The row itself, for the reason `deleting` holds one.
  const [endingGuest, setEndingGuest] = useState<{
    row: CurrentAssessmentRow;
    guestAsk: GuestAsk;
  } | null>(null);
  const toast = useToast();

  const startBlocked =
    starting?.definitive !== undefined && starting.definitiveUnderWay;

  // A definitive assessment started elsewhere disables the start button, so
  // the chooser it opened goes too. The server announces a start before it
  // answers, so the chooser's own start is left to answer first.
  if (choosingVersion && !startInFlight && startBlocked) {
    setChoosingVersion(false);
  }

  const handlersOf = (row: CurrentAssessmentRow): RowHandlers => {
    const guestAsk =
      guestEnding === undefined ? undefined : endableGuestAsk(row);

    return {
      onDelete: () => {
        setDeleting(row);
      },
      onEndGuestAccess:
        guestAsk === undefined
          ? undefined
          : () => {
              setEndingGuest({ row, guestAsk });
            },
    };
  };
  const endingGuestName =
    endingGuest?.row.assessorName ??
    t("personPage.assessments.unknownAssessor");

  const content = (): ReactNode => {
    const loading = (
      <Typography variant="body2" color="text.secondary">
        {t("common.loading")}
      </Typography>
    );

    if (rows === undefined) {
      return failed ? null : loading;
    }

    if (rows.length === 0) {
      // Which empty state applies depends on the profile, which is not known
      // until its involvements have been read.
      return state === undefined ? (
        loading
      ) : (
        <EmptyState state={state} caseInvolvementHref={caseInvolvementHref} />
      );
    }

    if (stacked) {
      return (
        <Stack divider={<Divider />}>
          {rows.map((row) => (
            <StackedRow
              key={row.assessment.id}
              row={row}
              {...handlersOf(row)}
            />
          ))}
        </Stack>
      );
    }

    return (
      <ActionTable
        columns={COLUMNS.map((column) =>
          t(`personPage.assessments.column.${column}`),
        )}
        actionsLabel={t("personPage.assessments.column.actions")}
      >
        {rows.map((row) => (
          <TableLayoutRow
            key={row.assessment.id}
            row={row}
            {...handlersOf(row)}
          />
        ))}
      </ActionTable>
    );
  };

  return (
    <Paper ref={ref} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Typography variant="h6" component="h2">
            {t("personPage.assessments.title")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {guestAsking === undefined || personName === undefined ? null : (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setAskingGuest(true);
                }}
              >
                {t("askGuest.open")}
              </Button>
            )}
            {starting === undefined ? null : (
              <Stack spacing={0.5} alignItems="flex-end">
                <Button
                  variant="contained"
                  size="small"
                  disabled={startBlocked}
                  onClick={() => {
                    setChoosingVersion(true);
                  }}
                >
                  {t("personPage.assessments.start")}
                </Button>
                {startBlocked ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("personPage.assessments.definitiveUnderWay")}
                  </Typography>
                ) : null}
              </Stack>
            )}
          </Stack>
        </Stack>

        {failed ? (
          <Alert severity="error">
            {t("personPage.assessments.loadFailed")}
          </Alert>
        ) : null}

        {content()}
      </Stack>

      {guestAsking === undefined ||
      !askingGuest ||
      personName === undefined ? null : (
        <AskGuestDialog
          open
          personName={personName}
          versions={guestAsking.versions}
          versionsFailed={guestAsking.versionsFailed}
          onMatch={guestAsking.onMatch}
          onAsk={async (request) => {
            const outcome = await guestAsking.onAsk(request);

            if (outcome.kind === "asked") {
              setAskingGuest(false);
              toast.show(
                t("askGuest.sent", {
                  guest: request.name,
                  date: calendarDayText(request.endsOn),
                }),
              );
            }

            return outcome;
          }}
          onClose={() => {
            setAskingGuest(false);
          }}
        />
      )}

      {starting === undefined ? null : (
        <StartAssessmentDialog
          open={choosingVersion}
          personName={starting.personName}
          definitive={starting.definitive}
          versions={starting.versions}
          versionsFailed={starting.versionsFailed}
          previous={starting.previous}
          onClose={() => {
            setChoosingVersion(false);
          }}
          onStart={async (version, carryOver) => {
            setStartInFlight(true);

            try {
              return await starting.onStart(version, carryOver);
            } finally {
              setStartInFlight(false);
            }
          }}
        />
      )}

      <DeleteAssessmentDialog
        assessment={deleting?.assessment ?? null}
        assessorName={deleting?.assessorName}
        personName={personName}
        readProgress={readProgress}
        onClose={() => {
          setDeleting(null);
        }}
        onDelete={onDelete}
      />

      {guestEnding === undefined ? null : (
        <EndGuestAccessDialog
          guest={
            endingGuest === null
              ? null
              : { kind: "complete_assessment", guestName: endingGuestName }
          }
          onClose={() => {
            setEndingGuest(null);
          }}
          onEnd={async () => {
            // The dialog is open only while a guest is chosen.
            /* v8 ignore if */
            if (endingGuest === null) {
              throw new Error("End was confirmed with no guest chosen");
            }

            const outcome = await guestEnding.onEnd(
              endingGuest.guestAsk.involvementId,
            );

            if (outcome === "ended") {
              toast.show(t("endGuestAccess.ended", { name: endingGuestName }));
            }

            return outcome;
          }}
        />
      )}
    </Paper>
  );
};
